import { z } from "zod";

/**
 * Mehrere Lager je Kunde: welches Lager versendet einen Auftrag?
 * Grundsätze: ein Auftrag geht immer komplett an genau ein Lager (keine Teillieferungen),
 * Länderregeln vor Hauptlager vor weiteren Lagern; ein Lager wird nur gewählt, wenn es alle Positionen
 * vorrätig hat – sonst (mit Ausweichen) das nächste, das alles hat; hat keines alles, bleibt es beim bevorzugten.
 */

export const warehouseEntrySchema = z.object({
  warehouseId: z.string().min(1),
  name: z.string().default(""),
  active: z.boolean().default(true),
  /** kleinere Zahl = früher gefragt (nach dem Hauptlager) */
  priority: z.number().int().default(0),
  /** Versandarten, die FFN diesem Kunden in diesem Lager freigibt (Stand des letzten Abrufs) */
  availableMethodIds: z.array(z.string()).optional(),
});
export type WarehouseEntry = z.infer<typeof warehouseEntrySchema>;

export const warehouseRoutingSchema = z.object({
  /** z. B. CH, LI → Lager in der Schweiz */
  countryRules: z.array(z.object({ countries: z.array(z.string().length(2)).min(1), warehouseId: z.string().min(1) })).default([]),
  /** anderes Lager nehmen, wenn das bevorzugte nicht alles vorrätig hat */
  fallbackToOther: z.boolean().default(true),
});
export type WarehouseRouting = z.infer<typeof warehouseRoutingSchema>;

/** Lager einer Verbindung, abwärtskompatibel: ohne Liste gilt das eine verbundene Lager */
export function warehousesOf(config: unknown): { primaryId: string | null; warehouses: WarehouseEntry[]; routing: WarehouseRouting } {
  const c = (config ?? {}) as { warehouseId?: string; warehouseName?: string; warehouses?: unknown[]; routing?: unknown; availableMethodIds?: string[] };
  const list = (c.warehouses ?? []).flatMap((w) => {
    const p = warehouseEntrySchema.safeParse(w);
    return p.success ? [p.data] : [];
  });
  if (c.warehouseId && !list.some((w) => w.warehouseId === c.warehouseId))
    list.unshift({ warehouseId: c.warehouseId, name: c.warehouseName ?? "", active: true, priority: 0, ...(c.availableMethodIds ? { availableMethodIds: c.availableMethodIds } : {}) });
  return { primaryId: c.warehouseId ?? list.find((w) => w.active)?.warehouseId ?? null, warehouses: list, routing: warehouseRoutingSchema.parse(c.routing ?? {}) };
}

export interface WarehouseChoiceInput {
  country: string;
  /** benötigte Menge je Artikel (Sets bereits in Komponenten aufgelöst) */
  needs: Map<string, number>;
  /** verfügbarer Bestand je Lager und Artikel */
  stock: Map<string, Map<string, number>>;
  primaryId: string;
  warehouses: WarehouseEntry[];
  routing: WarehouseRouting;
}

export interface WarehouseChoice {
  warehouseId: string;
  reason: string;
  /** geprüfte Lager in Reihenfolge, mit fehlenden Artikeln */
  checked: { warehouseId: string; complete: boolean; missing: string[] }[];
}

export function chooseWarehouse(i: WarehouseChoiceInput): WarehouseChoice {
  const active = i.warehouses.filter((w) => w.active);
  const name = (id: string) => active.find((w) => w.warehouseId === id)?.name || id;
  const rule = i.routing.countryRules.find((r) => r.countries.includes(i.country.toUpperCase()) && active.some((w) => w.warehouseId === r.warehouseId));
  const order = [
    ...(rule ? [rule.warehouseId] : []),
    i.primaryId,
    ...active.filter((w) => w.warehouseId !== i.primaryId).sort((a, b) => a.priority - b.priority).map((w) => w.warehouseId),
  ].filter((id, k, all) => all.indexOf(id) === k && active.some((w) => w.warehouseId === id));
  const preferred = order[0] ?? i.primaryId;
  if (order.length <= 1) return { warehouseId: preferred, reason: rule ? `Länderregel ${i.country}: ${name(preferred)}` : `einziges Lager: ${name(preferred)}`, checked: [] };

  const checked = order.map((id) => {
    const s = i.stock.get(id) ?? new Map<string, number>();
    const missing = [...i.needs].filter(([key, qty]) => (s.get(key) ?? 0) < qty).map(([key]) => key);
    return { warehouseId: id, complete: missing.length === 0, missing };
  });
  const first = checked[0]!;
  const why = rule ? `Länderregel ${i.country}` : "Hauptlager";
  if (first.complete) return { warehouseId: first.warehouseId, reason: `${why}: ${name(first.warehouseId)} hat alles vorrätig`, checked };
  if (i.routing.fallbackToOther) {
    const alt = checked.find((c) => c.complete);
    if (alt) return { warehouseId: alt.warehouseId, reason: `${name(first.warehouseId)} hat nicht alles vorrätig – ${name(alt.warehouseId)} versendet komplett`, checked };
  }
  return { warehouseId: first.warehouseId, reason: `${why}: ${name(first.warehouseId)}${i.routing.fallbackToOther ? " – kein Lager hat alles vorrätig" : ""}`, checked };
}
